import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Header, Footer } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Loader2, ArrowLeft, Star, CheckCircle2, X, Sparkles, RefreshCw, Trash2 } from 'lucide-react';
import authAxios, { API } from '../../utils/authAxios';
import { toast } from 'sonner';

const STATUS_COLORS = {
  pending: 'bg-slate-700 text-slate-200',
  approved: 'bg-emerald-500/20 text-emerald-300 border border-emerald-600/40',
  featured: 'bg-amber-500/20 text-amber-300 border border-amber-500/40',
  rejected: 'bg-red-500/20 text-red-300 border border-red-600/40',
};

const TABS = [
  { key: 'pending', label: 'Pendientes' },
  { key: 'approved', label: 'Aprobados' },
  { key: 'featured', label: 'Destacados' },
  { key: 'rejected', label: 'Rechazados' },
  { key: 'all', label: 'Todos' },
];

export default function Reto21TestimoniosAdmin() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [tab, setTab] = useState('pending');
  const [editing, setEditing] = useState(null);  // {id, edited_texto}

  const load = async () => {
    setLoading(true);
    try {
      const params = tab === 'all' ? {} : { status: tab };
      const res = await authAxios.get(API + '/admin/reto21/testimonios', { params });
      setItems(res.data.items || []);
      setStats(res.data.stats || {});
    } catch (e) {
      toast.error('No se pudieron cargar los testimonios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const moderate = async (id, status, extra = {}) => {
    try {
      await authAxios.patch(`${API}/admin/reto21/testimonios/${id}`, { status, ...extra });
      toast.success(`Marcado como ${status}`);
      setEditing(null);
      load();
    } catch {
      toast.error('No se pudo actualizar');
    }
  };

  const removeOne = async (id) => {
    if (!window.confirm('¿Eliminar este testimonio? Esta acción no se puede deshacer.')) return;
    try {
      await authAxios.delete(`${API}/admin/reto21/testimonios/${id}`);
      toast.success('Eliminado');
      load();
    } catch {
      toast.error('No se pudo eliminar');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <Link to="/admin" className="text-emerald-400 hover:text-emerald-300 text-sm inline-flex items-center gap-1 mb-2" data-testid="back-admin">
              <ArrowLeft className="w-4 h-4" /> Volver al panel
            </Link>
            <h1 className="text-3xl font-bold text-white">Reto 21 — Testimonios</h1>
            <p className="text-slate-400 mt-1">Modera, edita y destaca los testimonios enviados por los participantes.</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} className="border-slate-700 text-slate-200 hover:bg-slate-800" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Actualizar
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 border-b border-slate-800">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`px-4 py-2 text-sm rounded-t-lg border-b-2 transition ${
                tab === t.key
                  ? 'border-emerald-500 text-emerald-300 bg-slate-900/40'
                  : 'border-transparent text-slate-400 hover:text-slate-200'
              }`}
            >
              {t.label}
              {stats[t.key] != null && (
                <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-slate-800 text-slate-300">{stats[t.key]}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
        ) : items.length === 0 ? (
          <Card className="bg-slate-900/60 border-slate-800"><CardContent className="p-10 text-center text-slate-400" data-testid="empty-state">Sin testimonios en esta pestaña.</CardContent></Card>
        ) : (
          <div className="space-y-3" data-testid="testimonios-list">
            {items.map((it) => (
              <Card key={it.id} className="bg-slate-900/60 border-slate-800" data-testid={`testimonio-${it.id}`}>
                <CardContent className="p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <p className="text-white font-semibold">{it.nombre_display || 'Anónimo'}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[it.status] || ''}`}>{it.status}</span>
                        {it.rating && (
                          <span className="flex items-center gap-0.5 text-amber-400 text-xs">
                            {Array.from({ length: it.rating }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-amber-400" />)}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">
                        {it.edad ? `${it.edad} años · ` : ''}
                        {it.region ? `${it.region} · ` : ''}
                        {it.sustancia ? `${it.sustancia} · ` : ''}
                        {it.dias_completados ? `Día ${it.dias_completados} · ` : ''}
                        {new Date(it.created_at).toLocaleDateString('es-CL')}
                      </p>
                    </div>
                    <div className="flex gap-1.5 items-center">
                      {it.permite_publicar ? (
                        <span className="text-[10px] text-emerald-400 px-2 py-0.5 rounded bg-emerald-500/10 border border-emerald-700/40">permite publicar</span>
                      ) : (
                        <span className="text-[10px] text-red-300 px-2 py-0.5 rounded bg-red-500/10 border border-red-700/40">NO publicar</span>
                      )}
                    </div>
                  </div>

                  {editing?.id === it.id ? (
                    <div className="space-y-3">
                      <textarea
                        rows={5}
                        value={editing.edited_texto}
                        onChange={(e) => setEditing({ ...editing, edited_texto: e.target.value })}
                        className="w-full px-3 py-2 bg-slate-800/60 border border-slate-700 rounded-md text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        data-testid={`edit-textarea-${it.id}`}
                      />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => moderate(it.id, it.status, { edited_texto: editing.edited_texto })} className="bg-emerald-500 hover:bg-emerald-600" data-testid={`save-edit-${it.id}`}>
                          Guardar edición
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditing(null)} className="border-slate-700 text-slate-200 hover:bg-slate-800">Cancelar</Button>
                      </div>
                    </div>
                  ) : (
                    <blockquote className="border-l-2 border-slate-700 pl-3 my-2 text-slate-200 text-sm italic leading-relaxed whitespace-pre-wrap">
                      {it.edited_texto || it.texto}
                    </blockquote>
                  )}

                  {editing?.id !== it.id && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {it.status !== 'approved' && (
                        <Button size="sm" onClick={() => moderate(it.id, 'approved')} className="bg-emerald-500/90 hover:bg-emerald-500 text-white" data-testid={`approve-${it.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Aprobar
                        </Button>
                      )}
                      {it.status !== 'featured' && (
                        <Button size="sm" onClick={() => moderate(it.id, 'featured')} className="bg-amber-500/90 hover:bg-amber-500 text-black" data-testid={`feature-${it.id}`}>
                          <Sparkles className="w-3.5 h-3.5 mr-1" /> Destacar
                        </Button>
                      )}
                      {it.status !== 'rejected' && (
                        <Button size="sm" onClick={() => moderate(it.id, 'rejected')} variant="outline" className="border-red-500/40 text-red-300 hover:bg-red-500/10" data-testid={`reject-${it.id}`}>
                          <X className="w-3.5 h-3.5 mr-1" /> Rechazar
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => setEditing({ id: it.id, edited_texto: it.edited_texto || it.texto })} className="border-slate-700 text-slate-200 hover:bg-slate-800" data-testid={`edit-${it.id}`}>
                        Editar texto
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => removeOne(it.id)} className="text-red-400 hover:bg-red-500/10" data-testid={`delete-${it.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
